import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Upload, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { BackBar } from "../../components/BackBar";

const PROGRAMS = ["fluent_track", "bridge_track"];
const LEVELS = ["native", "advanced", "intermediate", "beginner", "none"];

export default function ApplicationPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [app, setApp] = useState(null);
  const [form, setForm] = useState({
    family_info: {},
    student_info: {},
    academic_background: {},
    language_profile: {},
    program_choice: "",
    parent_expectations: "",
    special_needs: "",
    consent: false,
    documents: [],
  });

  useEffect(() => {
    if (isNew) return;
    api.get(`/applications/${id}`).then(r => {
      const a = r.data;
      setApp(a);
      setForm({
        family_info: a.family_info || {},
        student_info: a.student_info || {},
        academic_background: a.academic_background || {},
        language_profile: a.language_profile || {},
        program_choice: a.program_choice || "",
        parent_expectations: a.parent_expectations || "",
        special_needs: a.special_needs || "",
        consent: !!a.consent,
        documents: a.documents || [],
      });
    }).catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [id, isNew]);

  const update = (section, key, value) => {
    setForm(f => ({ ...f, [section]: { ...f[section], [key]: value } }));
  };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Max 2.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(f => ({ ...f, documents: [...f.documents, { type: "report_card", filename: file.name, base64: reader.result, uploaded_at: new Date().toISOString() }] }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (asSubmitted) => {
    setSubmitting(true);
    try {
      const payload = { ...form, consent: asSubmitted ? true : form.consent };
      let appId = id;
      if (isNew) {
        const { data } = await api.post("/applications", payload);
        appId = data.id;
      }
      if (asSubmitted) {
        // 1. Mark consent
        await api.patch(`/applications/${appId}/submit`);
        trackEvent("application_submitted", {
          track: form.family_info?.track || "unknown",
          grade: form.student_info?.grade || "unknown",
        });
        // 2. Trigger Stripe 75€ application fee checkout
        const { data: checkout } = await api.post(
          `/applications/${appId}/fee/checkout`,
          { application_id: appId, origin_url: window.location.origin },
        );
        if (checkout?.url) {
          window.location.href = checkout.url;
          return;
        }
        toast.error("Erreur Stripe — réessayez");
      } else {
        toast.success("Saved");
        if (isNew) nav(`/parent/application/${appId}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12">{t("common.loading")}</div>;

  const steps = [
    t("appSteps.family"),
    t("appSteps.student"),
    t("appSteps.academic"),
    t("appSteps.language"),
    t("appSteps.program"),
    t("appSteps.documents"),
    t("appSteps.consent"),
  ];
  const isReadOnly = app && app.status !== "started";

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8" data-testid="application-page">
      <BackBar to="/parent" fallbackLabelFr="Tableau de bord" fallbackLabelEn="Dashboard" />
      <p className="overline mb-2">{t("parent.myApplication")}</p>
      <div className="flex items-baseline justify-between mb-8">
        <h1 className="font-display font-bold text-4xl tracking-tighter">{form.student_info?.first_name || "—"} {form.student_info?.last_name || ""}</h1>
        {app && <Badge>{t(`statuses.${app.status}`)}</Badge>}
      </div>

      {/* Stepper */}
      <div className="flex gap-2 mb-10 flex-wrap">
        {steps.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i)} data-testid={`step-${i}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${i === step ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{i+1}. {s}</button>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-serif tracking-tight text-2xl">{steps[step]}</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldInput readOnly={isReadOnly} label="Parent first name" value={form.family_info.parent_first_name || ""} onChange={v => update("family_info", "parent_first_name", v)} testid="ff-pfirst" />
              <FieldInput readOnly={isReadOnly} label="Parent last name" value={form.family_info.parent_last_name || ""} onChange={v => update("family_info", "parent_last_name", v)} testid="ff-plast" />
              <FieldInput readOnly={isReadOnly} label={t("common.email")} value={form.family_info.email || ""} onChange={v => update("family_info", "email", v)} testid="ff-email" />
              <FieldInput readOnly={isReadOnly} label={t("common.phone")} value={form.family_info.phone || ""} onChange={v => update("family_info", "phone", v)} testid="ff-phone" />
              <FieldInput readOnly={isReadOnly} label={t("common.country")} value={form.family_info.country || ""} onChange={v => update("family_info", "country", v)} testid="ff-country" />
            </div>
          )}
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldInput readOnly={isReadOnly} label="First name" value={form.student_info.first_name || ""} onChange={v => update("student_info", "first_name", v)} testid="fs-first" />
              <FieldInput readOnly={isReadOnly} label="Last name" value={form.student_info.last_name || ""} onChange={v => update("student_info", "last_name", v)} testid="fs-last" />
              <FieldInput readOnly={isReadOnly} type="date" label={t("inquiry.dateOfBirth")} value={form.student_info.date_of_birth || ""} onChange={v => update("student_info", "date_of_birth", v)} testid="fs-dob" />
              <FieldInput readOnly={isReadOnly} label={t("inquiry.currentGrade")} value={form.student_info.grade || ""} onChange={v => update("student_info", "grade", v)} testid="fs-grade" />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <FieldInput readOnly={isReadOnly} label="Previous school" value={form.academic_background.previous_school || ""} onChange={v => update("academic_background", "previous_school", v)} testid="fa-school" />
              <FieldInput readOnly={isReadOnly} label="Average grade" value={form.academic_background.average_grade || ""} onChange={v => update("academic_background", "average_grade", v)} testid="fa-grade" />
              <div>
                <Label className="text-sm">Special needs / accommodations</Label>
                <Textarea data-testid="fa-special" disabled={isReadOnly} value={form.special_needs} onChange={(e) => setForm(f => ({ ...f, special_needs: e.target.value }))} />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {lang === "fr"
                  ? "Indiquez le niveau actuel de votre enfant dans chaque langue. Ces informations nous aident à recommander le bon Track."
                  : "Indicate your child's current level in each language. This helps us recommend the right Track."}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">🇫🇷 {t("inquiry.frenchLevel")}</Label>
                  <Select disabled={isReadOnly} value={form.language_profile.french_level || ""} onValueChange={v => update("language_profile", "french_level", v)}>
                    <SelectTrigger data-testid="fl-fr"><SelectValue placeholder={lang === "fr" ? "— Sélectionner —" : "— Select —"} /></SelectTrigger>
                    <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{t(`levels.${l}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1.5 block">🇬🇧 {t("inquiry.englishLevel")}</Label>
                  <Select disabled={isReadOnly} value={form.language_profile.english_level || ""} onValueChange={v => update("language_profile", "english_level", v)}>
                    <SelectTrigger data-testid="fl-en"><SelectValue placeholder={lang === "fr" ? "— Sélectionner —" : "— Select —"} /></SelectTrigger>
                    <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{t(`levels.${l}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/30 rounded-lg p-4 text-sm space-y-2">
                <div className="font-semibold text-primary">
                  {lang === "fr" ? "Deux Tracks · un même Bilingual High School Diploma américain" : "Two Tracks · one American Bilingual High School Diploma"}
                </div>
                <div className="text-muted-foreground text-xs leading-relaxed">
                  {lang === "fr"
                    ? "Les deux parcours mènent au même diplôme américain. Ils diffèrent par le niveau d'anglais à l'entrée et l'intensité d'accompagnement linguistique."
                    : "Both pathways lead to the same American diploma. They differ in entry English level and linguistic support intensity."}
                </div>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                  <li><b className="text-foreground">Fluent Track</b> — {lang === "fr" ? "anglais avancé / natif. Maintien et approfondissement académique." : "advanced / native English. Maintain and deepen academically."}</li>
                  <li><b className="text-foreground">Bridge Track</b> — {lang === "fr" ? "anglais intermédiaire ou en construction. Pont structuré vers le bilinguisme." : "intermediate or building English. Structured bridge to bilingualism."}</li>
                </ul>
              </div>
              <div>
                <Label className="text-sm font-semibold mb-1.5 block">{t("inquiry.programInterest")}</Label>
                <Select disabled={isReadOnly} value={form.program_choice} onValueChange={v => setForm(f => ({ ...f, program_choice: v }))}>
                  <SelectTrigger data-testid="fp-program"><SelectValue placeholder={lang === "fr" ? "— Choisir un Track —" : "— Choose a Track —"} /></SelectTrigger>
                  <SelectContent>{PROGRAMS.map(p => <SelectItem key={p} value={p}>{t(`programs.${p}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">{lang === "fr" ? "Attentes des parents" : "Parent expectations"}</Label>
                <Textarea data-testid="fp-expectations" disabled={isReadOnly} value={form.parent_expectations} onChange={(e) => setForm(f => ({ ...f, parent_expectations: e.target.value }))} />
              </div>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/30 rounded-lg p-4 text-sm">
                <div className="font-semibold text-primary mb-1">{t("appSteps.docsTitle")}</div>
                <div className="text-muted-foreground">{t("appSteps.docsHelp")}</div>
              </div>
              {!isReadOnly && (
                <label className="border-2 border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-accent transition-colors" data-testid="upload-zone">
                  <Upload className="w-6 h-6 text-muted-foreground mb-2" strokeWidth={1.5} />
                  <span className="text-sm text-muted-foreground">{t("appSteps.docsCta")}</span>
                  <input type="file" className="hidden" onChange={onFile} accept="image/*,application/pdf" data-testid="upload-input" />
                </label>
              )}
              <ul className="space-y-2">
                {form.documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm border border-border rounded-md p-3 bg-secondary/30" data-testid={`doc-${i}`}>
                    <Check className="w-4 h-4 text-accent" />
                    <span className="flex-1 truncate">{d.filename}</span>
                    <Badge variant="outline">{d.type}</Badge>
                  </li>
                ))}
                {form.documents.length === 0 && <li className="text-xs text-muted-foreground">No documents yet.</li>}
              </ul>
            </div>
          )}
          {step === 6 && (
            <div className="space-y-4">
              <div className="bg-secondary/40 p-4 rounded-md text-sm leading-relaxed">
                {lang === "fr"
                  ? "Je confirme que les informations fournies sont exactes. Je consens à ce qu'OFA France traite cette candidature conformément à la réglementation sur la vie privée."
                  : "I confirm that the information provided is accurate. I consent to OFA France processing this application in accordance with privacy regulations."}
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox data-testid="fc-consent" checked={form.consent} onCheckedChange={(v) => setForm(f => ({ ...f, consent: !!v }))} disabled={isReadOnly} />
                <span className="text-sm">{lang === "fr" ? "J'accepte" : "I agree"}</span>
              </label>
              <div
                data-testid="application-fee-notice"
                className="mt-4 p-4 rounded-xl border-2"
                style={{
                  borderColor: "hsl(var(--accent) / 0.4)",
                  background: "hsl(var(--accent) / 0.08)",
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl font-display font-bold text-accent leading-none mt-0.5">75&nbsp;€</div>
                  <div className="text-sm flex-1">
                    <div className="font-semibold mb-1">
                      {lang === "fr" ? "Frais de dossier & évaluation" : "Application & evaluation fee"}
                    </div>
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {lang === "fr"
                        ? "Réglés par carte au moment de la soumission via Stripe (sécurisé). Non-remboursables — ils couvrent l'examen du dossier et l'évaluation de votre enfant par notre équipe d'admissions."
                        : "Paid by card at submission via Stripe (secure). Non-refundable — they cover application review and your child's evaluation by our admissions team."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-4 mt-6 flex justify-between gap-2 bg-card border border-border rounded-full shadow-lg p-2 z-10">
        <Button
          variant="ghost"
          size="lg"
          className="rounded-full"
          data-testid="prev-step"
          disabled={step === 0}
          onClick={() => setStep(s => s - 1)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t("common.back")}
        </Button>
        <div className="flex-1 text-center self-center text-xs text-muted-foreground tabular-nums">
          {lang === "fr" ? "Étape" : "Step"} {step + 1} / {steps.length}
        </div>
        <div className="flex gap-2">
          {step < steps.length - 1 ? (
            <Button data-testid="next-step" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setStep(s => s + 1)}>
              {t("common.next")} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : isReadOnly ? null : isNew ? (
            <Button data-testid="save-draft" size="lg" className="rounded-full" disabled={submitting} onClick={() => submit(false)}>{t("common.save")}</Button>
          ) : (
            <Button data-testid="submit-app" size="lg" disabled={submitting || !form.consent} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => submit(true)}>
              {submitting ? "…" : (lang === "fr" ? "Soumettre & régler 75 €" : "Submit & pay €75")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", readOnly, testid }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} disabled={readOnly} data-testid={testid} />
    </div>
  );
}
